import { TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';
import { Component, Inject, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TAGLI_CONTANTI } from '../../../lib/constants';

export interface CashBreakdownRow {
  descrizione: string;
  valoreTaglio: number;
  quantita: number;
  importo: number;
}

export interface CashBreakdownResult {
  righe: CashBreakdownRow[];
  totale: number;
}

export interface CashBreakdownData {
  righe?: { valoreTaglio: number; quantita: number }[];
  importoMonete?: number;
  maxContanti?: number | null;
}

@Component({
  providers: [provideTranslocoScope('receipts')],
  selector: 'rev-receipts-cash-breakdown-dialog-component',
  imports: [TranslocoPipe, FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule],
  templateUrl: './receipts-cash-breakdown-dialog-component.html',
  styleUrl: './receipts-cash-breakdown-dialog-component.scss',
})
export class receiptsCashBreakdownDialogComponent {
  readonly banknoteRows = TAGLI_CONTANTI
    .filter(taglio => taglio.importoTaglio > 0)
    .map(taglio => ({ ...taglio, quantita: 0 }));
  coinAmount: number | null = null;
  readonly maxContanti: number | null;

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) data?: CashBreakdownData) {
    this.maxContanti = data?.maxContanti ?? null;

    if (!data) {
      return;
    }

    for (const row of this.banknoteRows) {
      row.quantita = data.righe?.find(r => r.valoreTaglio === row.importoTaglio)?.quantita ?? 0;
    }

    this.coinAmount = data.importoMonete || null;
  }

  getRowTotal(row: { importoTaglio: number; quantita: number | null }): number {
    return Math.max(0, Number(row.quantita) || 0) * row.importoTaglio;
  }

  incrementQuantity(row: { quantita: number }): void {
    row.quantita = Math.max(0, Number(row.quantita) || 0) + 1;
  }

  decrementQuantity(row: { quantita: number }): void {
    row.quantita = Math.max(0, (Number(row.quantita) || 0) - 1);
  }

  get calculatedTotal(): number {
    const banknotesTotal = this.banknoteRows.reduce(
      (total, row) => total + this.getRowTotal(row),
      0,
    );

    return banknotesTotal + Math.max(0, Number(this.coinAmount) || 0);
  }

  get exceedsMaxContanti(): boolean {
    return this.maxContanti !== null && this.calculatedTotal > this.maxContanti;
  }

  get result(): CashBreakdownResult {
    const righe = this.banknoteRows.map(row => ({
      descrizione: row.descrizioneTaglio,
      valoreTaglio: row.importoTaglio,
      quantita: Math.max(0, Number(row.quantita) || 0),
      importo: this.getRowTotal(row),
    }));

    if (Math.max(0, Number(this.coinAmount) || 0) > 0) {
      righe.push({
        descrizione: 'Monete',
        valoreTaglio: 0,
        quantita: 0,
        importo: Math.max(0, Number(this.coinAmount) || 0),
      });
    }

    return { righe, totale: this.calculatedTotal };
  }

  formatAmount(value: number): string {
    return `€ ${value.toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}
